import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { RxDownload } from '@react-icons/all-files/rx/RxDownload'

import api from '@disco/api/server'
import mixpanel from '@disco/api/mixpanel'
import { CONVERT_WAV_AIFF, IS_ARTIST } from '@disco/utils/featuresAccessLevel'
import { getNounForExt } from '@disco/utils/file-utils'
import { downloadInFrameIfSupported } from '@disco/utils/target-iframe'
import {
  getExtraFormat,
  getSortedDownloadLinks,
  getZipDownloadsLinks,
  isAggregatedSearchTrack,
} from '@disco/utils/tracks'
import { getFeatureData } from '@disco/state-management/selectors/business'

import type { PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'

import { useSelector } from 'hooks/use-redux'
import { Flex, useMenu } from 'components/ds'
import DropdownSubmenu from 'components/track-actions/submenu/dropdown-submenu'
import ModalDialog from 'components/ui/modal-dialog'
import PricingBadge from 'components/ui/pricing-badge'

type Props = {
  track: Track
  sourcePlaylistId?: PlaylistId | null
  onClick: () => void
  type?: string
  menuTitle?: string
  groupId?: number | null
  isInSharedChannel?: boolean
  ownerId?: number
  role?: string
}

const DownloadTrack = ({
  track,
  sourcePlaylistId,
  onClick,
  type,
  menuTitle,
  groupId,
  isInSharedChannel,
  ownerId,
  role,
}: Props): React.ReactNode => {
  const Menu = useMenu()
  const navigate = useNavigate()
  const [pendingVirus, setPendingVirus] = useState<{
    url: string
    title: React.ReactNode
  } | null>(null)

  const featureData = useSelector(state =>
    getFeatureData(state, { featureName: CONVERT_WAV_AIFF }),
  )
  const isArtist = useSelector(
    state => getFeatureData(state, { featureName: IS_ARTIST }).isFeatureEnabled,
  )

  const getMixpanelData = (ext: string) => {
    const data: Record<string, unknown> = {
      'Track ID': track.id,
      Catalog: track.origin?.source_library?.id,
      'Catalog Business': track.origin?.business?.id,
      Format: ext,
    }
    if (isAggregatedSearchTrack(track)) data['Context'] = 'Aggregated search'
    if (isInSharedChannel) {
      data['Context'] = 'Shared Channels'
      if (role === 'partner') {
        data['Channel ID'] = groupId
        data['Channel Owner'] = ownerId
      }
    }
    return data
  }

  const triggerDownload = (url: string) => {
    const a = document.createElement('a')
    a.href = url
    a.setAttribute('download', '')
    // String() keeps the pre-strictNullChecks behavior: the DOM coerced an
    // undefined return value to the literal string "undefined" on old iOS browsers
    a.target = String(downloadInFrameIfSupported())
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const onFileDownload = (ext: string, url: string) => {
    api.sendTrackDownloadEvent(track.id, 'in-app', {
      playlist_id: sourcePlaylistId,
      group_id: groupId,
      client_library_id: track.origin?.source_library?.id,
    })
    mixpanel.sendPureEvent('Download track in app', getMixpanelData(ext))
    triggerDownload(url)
    onClick()
  }

  const extraFormat = getExtraFormat(track)
  const { isTrial, isFeatureEnabled, badgeTitle } = featureData
  const badge = isArtist ? null : <PricingBadge title={badgeTitle} />

  let rawLinks: Array<{ title: string; url: string; ext?: string }>
  if (type === 'all-versions') {
    rawLinks = getZipDownloadsLinks(track.nesting_zip_urls, 'inapp')
  } else {
    rawLinks = getSortedDownloadLinks(track).map(link => ({
      title: link.ext.toUpperCase(),
      url: link.url,
      ext: link.ext,
    }))
  }

  const links: React.ReactNode[] = rawLinks.map((link, index) => {
    const { url, title, ext } = link

    if (extraFormat && title === extraFormat.toUpperCase()) {
      if (!isTrial && !isFeatureEnabled) {
        // User needs to contact sales — open Intercom if available
        return (
          <Menu.Item
            key={`download-${index}`}
            onSelect={() => {
              // @ts-ignore: Intercom is a global
              if (window.Intercom)
                window.Intercom(
                  'showNewMessage',
                  "I'd like to discuss upgrading to Pro to access WAV/AIFF conversion.",
                )
              onClick()
            }}
          >
            <Flex align="center" gap="2">
              <span>{title}</span>
              {badge}
            </Flex>
          </Menu.Item>
        )
      } else if (isTrial && !isFeatureEnabled) {
        return (
          <Menu.Item
            key={`download-${index}`}
            onSelect={() => {
              navigate('/settings/billing')
              onClick()
            }}
          >
            <Flex align="center" gap="2">
              <span>{title}</span>
              {badge}
            </Flex>
          </Menu.Item>
        )
      }
    }

    return (
      <Menu.Item
        key={`download-${index}`}
        onSelect={e => {
          if (track.virus) {
            e.preventDefault()
            setPendingVirus({ url, title: <span>{title}</span> })
          } else {
            onFileDownload(ext ?? '', url)
          }
        }}
      >
        {isTrial && badge ? (
          <Flex align="center" gap="2">
            <span>{title}</span>
            {badge}
          </Flex>
        ) : (
          title
        )}
      </Menu.Item>
    )
  })

  const fileNoun = getNounForExt(track.ext)
  const triggerTitle = (
    <Flex align="center" gap="2">
      <RxDownload />
      {menuTitle ?? <span>{`Download ${fileNoun}`}</span>}
    </Flex>
  )

  return (
    <>
      <DropdownSubmenu title={triggerTitle} links={links} />
      {pendingVirus && (
        <ModalDialog
          title={pendingVirus.title}
          primaryAction={{
            caption: 'Download',
            onClick: () => {
              triggerDownload(pendingVirus.url)
              setPendingVirus(null)
              onClick()
            },
          }}
          onCancel={() => setPendingVirus(null)}
        >
          This file has been marked as suspicious, and could contain a virus.
          Are you sure you want to download it?
        </ModalDialog>
      )}
    </>
  )
}

export default DownloadTrack
